import { Dialog, Transition } from "@headlessui/react";
import React, { FC, Fragment, memo, ReactNode } from "react";
import clsx from "clsx";

import CloseIcon from "/public/icon/close.svg";

export enum Sizes {
  SS = "SS",
  S = "S",
  M = "M",
  L = "L",
  XXXL = "3XL",
  XXXXL = "4XL",
  XXXXXL = "5XL",
  TUTORIAL_WINDOW = "tutorial-window",
  TUTORIAL_CREATE_CONTENT="tutorial-create-window",
  MAX_CONTENT = "max-content",
}

// モーダルサイズ定義
const ModifierClassNames = {
  [Sizes.SS]: "max-w-[380px]",
  [Sizes.S]: "max-w-[392px]",
  [Sizes.M]: "max-w-md",
  [Sizes.L]: "max-w-lg",
  [Sizes.XXXL]: "max-w-[680px]",
  [Sizes.XXXXL]: "max-w-[800px]",
  [Sizes.XXXXXL]: "max-w-[1040px]",
  [Sizes.TUTORIAL_WINDOW]: "max-w-[65%]",
  [Sizes.TUTORIAL_CREATE_CONTENT]: "max-w-[760px]",
  [Sizes.MAX_CONTENT]: "w-max",
};

export type SpSize = "M" | "full" | "mainBlock" | "tutorial";

interface Props {
  size?: Sizes;
  spSize?: SpSize;
  isOpen: boolean;
  handleClose: () => void;
  children: ReactNode;
  containerClassName?: string;
  className?: string;
}

interface ModalHeadProps {
  title?: ReactNode;
  onClickClose: () => void;
  isShowCloseBtn?: boolean;
  className?: string;
}

interface ModalFootProps {
  children: ReactNode;
  className?: string;
}

export const ModalHead: FC<ModalHeadProps> = ({
  title,
  onClickClose,
  isShowCloseBtn = true,
  className = "",
}: ModalHeadProps) => {
  return (
    <div
      className={`flex justify-between items-center py-4 pr-3 pl-5 border-b md:pr-4 ${className}`}
    >
      {title ? (
        <Dialog.Title
          as="h3"
          className="text-[22px] font-bold leading-6 md:text-xl"
        >
          {title}
        </Dialog.Title>
      ) : (
        <span />
      )}
      {isShowCloseBtn && (
        <button
          type="button"
          className="bg-white rounded-full border-0"
          onClick={onClickClose}
        >
          <CloseIcon className="w-8 h-8" title="閉じる" />
        </button>
      )}
    </div>
  );
};

export const ModalFoot: FC<ModalFootProps> = ({
  children,
  className = "",
}: ModalFootProps) => {
  return (
    <div className={`flex justify-end items-center p-4 border-t ${className}`}>
      {children}
    </div>
  );
};

export const spOverlaySizeToClass = (s?: SpSize): string => {
  switch (s) {
    case "full":
      return "md:p-4";
    case "mainBlock":
      return "px-4 pt-[66px] pb-[96px] md:py-4";
    case "tutorial":
      return "px-4 md:mt-[0px] pb-[96px] md:py-4";
    default:
      return "p-4 py-[120px]";
  }
};

const ModalBase: FC<Props> = ({
  size = Sizes.M,
  spSize = "M",
  isOpen,
  handleClose,
  children,
  containerClassName,
  className,
}: Props) => {
  // isOpen=false の時はDOMの追加をしない
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="overflow-y-auto fixed inset-0">
            <div
              className={clsx(
                "flex justify-center text-center md:mt-[120px]",
                spOverlaySizeToClass(spSize),
                containerClassName,
              )}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full text-left align-middle bg-white drop-shadow-[0_1px_5px_rgba(0,0,0,0.15)] transition-all ${
                    ModifierClassNames[size]
                  } ${
                    spSize === "full"
                      ? "min-h-screen md:h-auto md:rounded-lg md:min-h-0"
                      : "rounded-lg"
                  } ${className ?? ""}`}
                >
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export const Modal = memo(ModalBase);
